import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Zap, Shield, Star, Wrench, Package } from "lucide-react";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

type EntryType = "feature" | "improvement" | "fix" | "security" | "new";

interface ChangeEntry {
  type: EntryType;
  text: string;
}

interface Release {
  version: string;
  date: string;
  label?: string;
  changes: ChangeEntry[];
}

const RELEASES: Release[] = [
  {
    version: "1.7.0",
    date: "June 2026",
    label: "Latest",
    changes: [
      { type: "new", text: "Advanced Analytics tab in Reports — peak sales hours, payment method split, top products by revenue, avg transaction value trend" },
      { type: "new", text: "Brands page: click any brand row to expand and view all its products inline with price and stock" },
      { type: "new", text: "Categories page: click any category row to expand and view all its products inline; product count badge per row" },
      { type: "improvement", text: "Customer names now visible on the sales transaction list — shows 'Walk-in' for anonymous sales" },
      { type: "improvement", text: "Recent Sales on dashboard now shows customer name and receipt number instead of generic 'Sale #ID'" },
      { type: "improvement", text: "Demo section on landing page now supports two videos with a toggle tab switcher" },
    ],
  },
  {
    version: "1.6.0",
    date: "June 2026",
    changes: [
      { type: "new", text: "Per-store Termii API key — TinyTrendyKiddies can send SMS from their own sender ID without sharing platform credentials" },
      { type: "new", text: "Enterprise landing page card now shows two CTAs: Subscribe Now (Paystack) and Contact Sales (WhatsApp)" },
      { type: "improvement", text: "Pricing page: each plan now shows an exclusive feature list (no 'everything in lower plan' clutter)" },
      { type: "improvement", text: "Coming Soon section added to landing page — Logistics and E-commerce teaser cards" },
      { type: "improvement", text: "SMS credits added to Pro (1,000/mo) and Enterprise (2,000/mo) plan cards" },
    ],
  },
  {
    version: "1.5.0",
    date: "May 2026",
    changes: [
      { type: "new", text: "Gift Vouchers — create, sell, and redeem vouchers at the POS" },
      { type: "new", text: "Attendance & Clock-In board on dashboard for Enterprise stores" },
      { type: "new", text: "Supplier Ledger — track what you owe each supplier with payment history" },
      { type: "new", text: "Fund Transfers — move money between payment methods with a full audit trail" },
      { type: "new", text: "Checklists — create daily opening/closing task lists for staff" },
      { type: "improvement", text: "Daily Close Report now exportable as PDF" },
    ],
  },
  {
    version: "1.4.0",
    date: "May 2026",
    changes: [
      { type: "new", text: "SMS Credits — send receipt and promotional SMS to customers via Termii" },
      { type: "new", text: "Payment Hub — unified view of all payment method balances and reconciliation" },
      { type: "new", text: "Unpaid Sales tracker — flag credit sales and track outstanding balances per customer" },
      { type: "new", text: "Customer loyalty points — earn and redeem points at checkout" },
      { type: "improvement", text: "Sales receipt now includes WhatsApp share button" },
      { type: "improvement", text: "Staff performance page shows per-cashier revenue breakdown" },
    ],
  },
  {
    version: "1.3.0",
    date: "April 2026",
    changes: [
      { type: "new", text: "Brands page — organise and rename product brands in bulk" },
      { type: "new", text: "Returns & Refunds — process full and partial returns; stock automatically restored" },
      { type: "new", text: "Accounting suite — Profit & Loss, Cash Flow, Balance Sheet, Expense Reports" },
      { type: "new", text: "Specialist dashboards — Fashion, Ready-to-Wear, Supermarket, Salon, Food & Beverage, Tailor" },
      { type: "improvement", text: "Low stock alerts now visible directly on the main dashboard" },
    ],
  },
  {
    version: "1.2.0",
    date: "March 2026",
    changes: [
      { type: "new", text: "Paystack subscription billing — Basic, Pro, and Enterprise plans with auto-renewal" },
      { type: "new", text: "7-day free trial on signup at launch (now 10 days) — no card required" },
      { type: "new", text: "Super-admin dashboard — view all stores, active subscribers, and platform revenue" },
      { type: "new", text: "Expense tracking with category breakdown" },
      { type: "new", text: "Purchase Orders — record stock purchases from suppliers" },
      { type: "improvement", text: "JWT auth now stored in httpOnly cookie for better security" },
    ],
  },
  {
    version: "1.1.0",
    date: "February 2026",
    changes: [
      { type: "new", text: "Full POS new-sale flow — barcode scan, quantity, discount, payment method, receipt print" },
      { type: "new", text: "Staff management — add staff, assign roles (admin / manager / staff)" },
      { type: "new", text: "Customer profiles — name, phone, email, purchase history" },
      { type: "new", text: "Reports: Revenue vs Expense chart, Top Products, Expense Breakdown pie chart" },
      { type: "improvement", text: "All prices stored and displayed in Nigerian Naira (₦)" },
    ],
  },
  {
    version: "1.0.0",
    date: "January 2026",
    changes: [
      { type: "new", text: "RetailPulse launched — Inventory, Sales, Categories, Suppliers, Dashboard" },
      { type: "new", text: "Multi-tenant architecture — every store gets its own isolated data" },
      { type: "new", text: "Signup creates a store + admin user in one step" },
      { type: "new", text: "OpenAPI-first backend with generated React Query hooks" },
    ],
  },
];

const TYPE_CONFIG: Record<EntryType, { label: string; color: string; icon: React.ReactNode }> = {
  new:         { label: "New",         color: "bg-emerald-100 text-emerald-700 border-emerald-200",  icon: <Star className="w-3 h-3" /> },
  feature:     { label: "Feature",     color: "bg-blue-100 text-blue-700 border-blue-200",           icon: <Zap className="w-3 h-3" /> },
  improvement: { label: "Improved",    color: "bg-violet-100 text-violet-700 border-violet-200",     icon: <Package className="w-3 h-3" /> },
  fix:         { label: "Fix",         color: "bg-orange-100 text-orange-700 border-orange-200",     icon: <Wrench className="w-3 h-3" /> },
  security:    { label: "Security",    color: "bg-red-100 text-red-700 border-red-200",              icon: <Shield className="w-3 h-3" /> },
};

export default function Changelog() {
  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16">
        <div className="mb-4">
          <Link href={`${BASE}/`}>
            <Button variant="ghost" size="sm" className="gap-1.5 -ml-2 text-muted-foreground">
              <ArrowLeft className="w-4 h-4" /> Back to Home
            </Button>
          </Link>
        </div>

        <div className="mb-12">
          <h1 className="text-4xl font-bold tracking-tight mb-3">Changelog</h1>
          <p className="text-muted-foreground text-lg">
            Every update, fix, and new feature shipped to RetailPulse — newest first.
          </p>
        </div>

        <div className="relative">
          <div className="absolute left-[7px] top-2 bottom-2 w-px bg-border" />

          <div className="space-y-12">
            {RELEASES.map((release) => (
              <div key={release.version} className="relative pl-8">
                <div className="absolute left-0 top-1.5 w-3.5 h-3.5 rounded-full bg-primary border-2 border-background ring-2 ring-primary/20" />

                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  <h2 className="text-xl font-bold">v{release.version}</h2>
                  {release.label && (
                    <Badge className="bg-primary/10 text-primary border-primary/20 text-xs">
                      {release.label}
                    </Badge>
                  )}
                  <span className="text-sm text-muted-foreground">{release.date}</span>
                </div>

                <ul className="space-y-3">
                  {release.changes.map((c, i) => {
                    const cfg = TYPE_CONFIG[c.type];
                    return (
                      <li key={i} className="flex items-start gap-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border flex-shrink-0 mt-0.5 ${cfg.color}`}>
                          {cfg.icon} {cfg.label}
                        </span>
                        <span className="text-sm leading-relaxed">{c.text}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-16 border-t pt-8 text-center">
          <p className="text-muted-foreground text-sm mb-4">Want to see what's coming next?</p>
          <Link href={`${BASE}/roadmap`}>
            <Button variant="outline">View Roadmap →</Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
